/**
 * What a Word List term means when "Regex Match" is off.
 *
 * `regex_any` / `regex_none` take `treat_as_regex` as their FOURTH parameter and
 * default it to **false** (`regex_matches`, the single-term operator, defaults it
 * to true -- the two are not alike). With it false, rust-smasher runs
 * `regex::escape(term)` before building the pattern
 * (`src/evaluation/evaluators/regex.rs`, `build_allow_pattern_no_lookaround`), so
 * every character the author typed is searched for as itself.
 *
 * That makes two kinds of term silently inert -- they are stored, they are
 * compiled, they cost a pass over every post, and they can never match:
 *
 *   glob   `foot*`, `*twitch*`, `application*` -- shell-style wildcards. Escaped,
 *          these look for a literal asterisk.
 *   regex  `\bSpring\s*Training\b`, `(?:Dbacks|D-Backs)` -- a pattern written out
 *          in full. Escaped, these look for a literal backslash-b.
 *
 * A `*` is NOT evidence of either on its own, which is the whole reason this
 * module exists rather than a `includes("*")`. The largest group of asterisks in
 * production sits between two word characters -- `f*cker`, `tr*mp`, `d*ldos`,
 * `(*^o^*)` -- where the author is censoring a word and wants the asterisk matched
 * exactly as typed. Flagging those would be telling people their working filter is
 * broken. So a wildcard is only read as a wildcard at a boundary: the start or end
 * of the term, or beside a space. A `*` immediately after a `.` is regex, not glob,
 * and is classified as such.
 *
 * Counted over `feed_db` on 2026-09-24, walking the patterns array rather than
 * text-matching the parameter blob: across every non-deleted literal-mode
 * condition, 89 carry glob terms and 665 carry written-out regexes; 327 of them
 * consist ENTIRELY of inert terms, so the node cannot fire at all. Restricted to
 * conditions a live published manifest actually references: 19 glob and 85 regex
 * conditions, and 61 wholly inert ones across 44 feeds and 39 owners.
 *
 * No path-alias imports on purpose: `scripts/verify-literal-terms.mjs` runs this
 * under bare node with types stripped.
 */

type InertKind = "glob" | "regex";

interface InertTerm {
  index: number;
  term: string;
  kind: InertKind;
}

/**
 * Rust's `regex::escape` meta set (`regex_syntax::is_meta_character`). Escaping
 * is what literal mode already does to every term, so reproducing the same set
 * is what lets `asRegexTerms` promise that nothing changes meaning except the
 * wildcards.
 */
const META = /[\\.+*?()|[\]{}^$#&~-]/g;

function escapeForRegex(term: string): string {
  return term.replace(META, "\\$&");
}

const isSpace = (char: string | undefined) =>
  char === undefined || /\s/.test(char);

/**
 * Offsets of the asterisks in `term` the author meant as wildcards.
 *
 * Boundary-anchored, and never one that follows a `.` -- `.*` is somebody writing
 * a regex, and `regexSyntax` below is what speaks for those.
 */
export function wildcardOffsets(term: string): number[] {
  const offsets: number[] = [];

  for (let i = 0; i < term.length; i++) {
    if (term[i] !== "*") continue;
    if (i > 0 && term[i - 1] === ".") continue;
    if (isSpace(term[i - 1]) || isSpace(term[i + 1])) offsets.push(i);
  }

  return offsets;
}

/**
 * Regex syntax that cannot be anything else. Deliberately narrow: `|`, `(` and
 * `.` occur in ordinary prose and are left alone, so a term is only called a
 * regex on a class escape, a group flag, a negated or ranged character class, a
 * `.*`/`.+`, or a repetition count.
 */
const REGEX_SYNTAX = /\\[bBdDsSwWAZzn]|\(\?|\[\^|\[[^\]]*-[^\]]*\]|\.[*+]|\{\d+,?\d*\}/;

export function regexSyntax(term: string): boolean {
  return REGEX_SYNTAX.test(term);
}

/** The terms of a literal-mode list that cannot match anything as typed. */
export function inertTerms(
  terms: readonly string[],
  treatAsRegex: boolean | undefined,
): InertTerm[] {
  if (treatAsRegex) return [];

  const inert: InertTerm[] = [];

  terms.forEach((term, index) => {
    if (typeof term !== "string" || term === "") return;

    if (wildcardOffsets(term).length > 0) {
      inert.push({ index, term, kind: "glob" });
    } else if (regexSyntax(term)) {
      inert.push({ index, term, kind: "regex" });
    }
  });

  return inert;
}

/**
 * The same list, rewritten so turning "Regex Match" on changes nothing except
 * making the wildcards work.
 *
 * Turning the toggle on by itself is not safe: it reinterprets EVERY term in the
 * node at once, and `Mother f*cker` read as a regex becomes "Mother f, zero or
 * more times, cker". So each term is handled by what it is:
 *
 *   glob   escaped, then the boundary asterisks put back as `.*`
 *          -- `foot*` becomes `foot.*`
 *   regex  left exactly as typed; it is already the pattern the author wanted
 *          -- `\bSpring\s*Training\b` is untouched
 *   other  escaped, so it goes on meaning precisely what it means today
 *          -- `Mother f*cker` becomes `Mother f\*cker`, `twitch.tv` becomes
 *             `twitch\.tv`
 */
export function asRegexTerms(terms: readonly string[]): string[] {
  return terms.map((term) => {
    if (typeof term !== "string") return term;

    const offsets = wildcardOffsets(term);

    if (offsets.length === 0) return regexSyntax(term) ? term : escapeForRegex(term);

    // Indexed in code units, matching `wildcardOffsets`. Splitting by code
    // point instead would slide every offset past the first astral character,
    // and these lists carry plenty of them.
    const wildcard = new Set(offsets);
    let out = "";

    for (let i = 0; i < term.length; i++) {
      out += wildcard.has(i) ? ".*" : escapeForRegex(term[i] as string);
    }

    return out;
  });
}

/**
 * Whether a term would compile as a pattern, as a proxy for the engine.
 *
 * The matcher is Rust's `regex` crate and this runs in a browser, so the two
 * dialects are close but not equal: `(?i)` and `(?-i)` as a leading flag group,
 * and `\u{3040}` scalar escapes, are ordinary Rust regex and are rejected by a
 * bare `new RegExp`. Both are tried for here, because refusing to offer the fix
 * on a pattern that the engine would have accepted is the more expensive
 * mistake -- over a scan of every literal-mode condition in `feed_db` that is
 * 1,259 terms, against 24 that no engine accepts (`.*jojo.*(adventure.*`,
 * `.*fanart).*` -- unbalanced groups, broken wherever they run).
 *
 * Deliberately lenient, in other words: a `false` here means the term is
 * malformed in every dialect, which is worth telling the author about before
 * the node is switched into a mode that would try to compile it.
 */
function compilesAsPattern(term: string): boolean {
  const withoutLeadingFlags = term.replace(/^\(\?-?[imsxU]+\)/, "");

  for (const candidate of [term, withoutLeadingFlags]) {
    try {
      new RegExp(candidate);
      return true;
    } catch {
      /* try the next dialect */
    }
    try {
      new RegExp(candidate, "u");
      return true;
    } catch {
      /* not this one either */
    }
  }

  return false;
}

/** The rewritten terms that no engine would accept, so the author can fix them first. */
export function uncompilableTerms(terms: readonly string[]): string[] {
  return asRegexTerms(terms).filter((term) => !compilesAsPattern(term));
}
